import {
  TrackClick,
  type EducatorTrackingLocation,
} from '@educator/tracking/educatorTracking'
import { LogoLinkedInMark } from '@mc/educator-design-system'
import { TestimonialAvatar } from './TestimonialAvatar'

const linkedInAction = (name: string) =>
  `linkedin profile - ${name.toLowerCase()}`

interface LinkedInBadgeProps {
  href: string
  label: string
  name: string
  location: EducatorTrackingLocation
}

const BADGE_CLASS =
  'inline-flex shrink-0 items-center gap-2 text-body-3 font-bold leading-body-3 text-edu-fg-primary no-underline'

// Plain badge (not an anchor) when there's no URL — avoids an empty-href link.
const LinkedInBadge = ({ href, label, name, location }: LinkedInBadgeProps) =>
  href ? (
    <TrackClick
      event='educatorSignClick'
      action={linkedInAction(name)}
      location={location}
    >
      <a
        href={href}
        target='_blank'
        rel='noopener noreferrer'
        className={BADGE_CLASS}
      >
        <LogoLinkedInMark
          srText=''
          width={18}
          height={18}
          className='shrink-0'
        />
        {label}
      </a>
    </TrackClick>
  ) : (
    <span className={BADGE_CLASS}>
      <LogoLinkedInMark srText='' width={18} height={18} className='shrink-0' />
      {label}
    </span>
  )

export interface TestimonialCardProps {
  id: string
  name: string
  role?: string
  quote: string
  linkedInUrl: string
  avatarUrl: string
  fromLinkedInLabel: string
  location: EducatorTrackingLocation
}

export const TestimonialCard = ({
  name,
  role,
  quote,
  linkedInUrl,
  avatarUrl,
  fromLinkedInLabel,
  location,
}: TestimonialCardProps) => (
  <article
    aria-label={name}
    className='flex h-[488px] w-[var(--peek-card-w)] shrink-0 flex-col gap-8 overflow-hidden bg-edu-bg-tertiary px-8 py-6 transition-colors duration-150 ease-out hover:bg-edu-brand-blue md:p-8'
  >
    <div className='flex items-center gap-4 md:justify-between'>
      <div className='flex items-center gap-4'>
        <TestimonialAvatar src={avatarUrl} name={name} />
        <div className='flex flex-col gap-1'>
          <p className='text-title-3 leading-title-3 text-edu-fg-primary'>
            {name}
          </p>
          {role && (
            <p className='text-body-3 leading-body-3 text-edu-fg-primary'>
              {role}
            </p>
          )}
        </div>
      </div>
      <div className='hidden md:block'>
        <LinkedInBadge
          href={linkedInUrl}
          label={fromLinkedInLabel}
          name={name}
          location={location}
        />
      </div>
    </div>

    {/* 22px matches Figma (no Boogie token for it). */}
    <p className='mt-auto text-body-3 leading-body-3 text-edu-fg-primary md:text-[22px] md:font-normal md:leading-[1.3]'>
      {quote}
    </p>

    <div className='md:hidden'>
      <LinkedInBadge
        href={linkedInUrl}
        label={fromLinkedInLabel}
        name={name}
        location={location}
      />
    </div>
  </article>
)
